import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { 
  Users, Gift, UserX, Clock, Ban, Shield, ShieldOff, 
  DollarSign, TrendingUp, AlertTriangle, CheckCircle, 
  UserCheck, Mail, FileText, Lock, Megaphone, KeyRound, Brain, Activity
} from 'lucide-react';

export default function HowToTab() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Admin Dashboard Guide</h2>
        <p className="text-emerald-50">Complete reference for all administrative features and functions</p>
      </div>

      {/* User Management Section */}
      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="w-5 h-5" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-500" />
              Grant Free 30-Day Subscription (Superuser Only)
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Give users 30 days of free access without payment</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Find the user and click <strong>Manage</strong></li>
                <li>Select <strong>"Grant Free 30-Day Subscription"</strong> from dropdown</li>
                <li>Click <strong>"Grant Free 30-Day Access"</strong> button</li>
              </ol>
              <p><strong>Use cases:</strong> Beta testers, promotions, customer service resolutions</p>
              <p className="text-amber-700">⚠️ <strong>Superuser only:</strong> Only maridjo@gmail.com can use this feature</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserX className="w-4 h-4 text-red-500" />
              Revoke Subscription (Superuser Only)
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Immediately cancel a user's active subscription</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Find user with active subscription</li>
                <li>Click <strong>Manage</strong></li>
                <li>Select <strong>"Revoke Subscription"</strong> from dropdown</li>
                <li>Review warning message</li>
                <li>Click <strong>"Revoke Subscription"</strong> button</li>
              </ol>
              <p><strong>What happens:</strong> User loses access immediately, status changes to "Expired"</p>
              <p className="text-red-700">⚠️ <strong>Warning:</strong> This action is immediate and cannot be undone. User will lose all access.</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-emerald-500" />
              Grant Full Access (Manual Activation)
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Manually activate subscription for any duration</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Click <strong>Manage</strong> on user</li>
                <li>Select <strong>"Grant Full Access (Manual)"</strong></li>
                <li>Choose duration: 30 days, 90 days, 180 days, 1 year, 2 years, or Lifetime</li>
                <li>Click <strong>"Grant Full Access"</strong></li>
              </ol>
              <p><strong>Available to:</strong> All admins</p>
              <p><strong>Recommended:</strong> Use 365 days (1 year) for standard subscriptions</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Extend Trial Period
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Give trial users additional days to evaluate the service</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> or <strong>Extensions</strong> tab</li>
                <li>Find user with trial status</li>
                <li>Click <strong>Manage</strong></li>
                <li>Select <strong>"Extend Trial"</strong></li>
                <li>Enter number of days (default: 5)</li>
                <li>Click <strong>"Extend Trial"</strong></li>
              </ol>
              <p><strong>Default extension:</strong> 5 days</p>
              <p><strong>Note:</strong> Users can request extensions themselves via the Subscribe page</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-500" />
              Ban/Unban Users
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Ban User:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Click <strong>Manage</strong> on user</li>
                <li>Select <strong>"Ban User"</strong></li>
                <li>Enter reason for ban (required)</li>
                <li>Click <strong>"Ban User"</strong></li>
              </ol>
              <p><strong>Unban User:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Filter users by "Banned" status</li>
                <li>Click <strong>Manage</strong> on banned user</li>
                <li>Select <strong>"Unban User"</strong></li>
                <li>Click <strong>"Unban User"</strong></li>
              </ol>
              <p><strong>Use cases:</strong> Terms violations, suspicious activity, payment fraud</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-500" />
              Send Password Reset Link
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Help users who forgot their password by sending a reset email</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Find the user and click <strong>Manage</strong></li>
                <li>Select <strong>"Send Password Reset Link"</strong> from dropdown</li>
                <li>Click <strong>"Send Password Reset Email"</strong></li>
              </ol>
              <p><strong>What happens:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>User receives email with 6-digit reset code</li>
                <li>Code is valid for 24 hours</li>
                <li>User clicks "Forgot Password?" on login page</li>
                <li>User enters code and creates new password</li>
              </ul>
              <p><strong>Use cases:</strong> Users locked out, forgotten passwords, account recovery</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-500" />
              Promote to Admin / Demote from Admin (Superuser Only)
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Promote to Admin:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Find regular user</li>
                <li>Click <strong>Manage</strong></li>
                <li>Select <strong>"Make Admin"</strong></li>
                <li>Click <strong>"Promote to Admin"</strong></li>
              </ol>
              <p><strong>Demote from Admin:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Find admin user (not yourself)</li>
                <li>Click <strong>Manage</strong></li>
                <li>Select <strong>"Remove Admin Role"</strong></li>
                <li>Click <strong>"Demote from Admin"</strong></li>
              </ol>
              <p className="text-amber-700">⚠️ <strong>Superuser only:</strong> Only maridjo@gmail.com can promote/demote admins</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extension Requests Section */}
      <Card>
        <CardHeader className="bg-yellow-50">
          <CardTitle className="flex items-center gap-2 text-yellow-900">
            <Clock className="w-5 h-5" />
            Extension Requests Management
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">How Users Request Extensions</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>User's flow:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>User's trial expires</li>
                <li>They see "Need More Time to Evaluate?" on Subscribe page</li>
                <li>They click <strong>"Request Extension"</strong> button</li>
                <li>Request is submitted to admin</li>
                <li>User sees "Extension Request Pending" message</li>
                <li>Admin reviews within 24 hours</li>
              </ol>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">How Admins Review Extensions</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Extensions</strong> tab</li>
                <li>See list of pending extension requests</li>
                <li>Review user information and trial history</li>
                <li>Click <strong>Manage</strong> on the request</li>
                <li>Select <strong>"Extend Trial"</strong></li>
                <li>Grant 5 days (or custom amount)</li>
                <li>Click <strong>"Extend Trial"</strong> to approve</li>
              </ol>
              <p><strong>Note:</strong> Users are automatically notified via email</p>
              <p><strong>Policy:</strong> Each user can only request ONE extension per account</p>
              <p><strong>SLA:</strong> Review within 24 hours of request</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Section */}
      <Card>
        <CardHeader className="bg-emerald-50">
          <CardTitle className="flex items-center gap-2 text-emerald-900">
            <DollarSign className="w-5 h-5" />
            Payment Management
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Manual Payment Recording</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Record payments received via GCash, bank transfer, or other methods</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Payments</strong> tab</li>
                <li>Click <strong>"Record Payment"</strong> button</li>
                <li>Select the user who paid</li>
                <li>Enter amount (₱699 for first year, ₱99 for renewal)</li>
                <li>Enter payment reference ID</li>
                <li>Select payment method (GCash, Bank Transfer, etc.)</li>
                <li>Click <strong>"Record Payment"</strong></li>
              </ol>
              <p><strong>What happens:</strong> User's subscription is activated for 1 year</p>
              <p><strong>Note:</strong> Payment is recorded in the system for tracking and reporting</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security & Account Protection */}
      <Card>
        <CardHeader className="bg-red-50">
          <CardTitle className="flex items-center gap-2 text-red-900">
            <Lock className="w-5 h-5" />
            Security & Account Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Session Limits (2 Concurrent Sessions)</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Policy:</strong> Each account is limited to 2 concurrent sessions</p>
              <p><strong>How it works:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>System tracks active logins per user</li>
                <li>Users can have max 2 sessions simultaneously</li>
                <li>3rd session login is blocked until user logs out from another session</li>
                <li>Sessions expire after 24 hours of inactivity</li>
              </ul>
              <p><strong>User sees:</strong> "Session limit exceeded. This account already has 2 active sessions..."</p>
              <p><strong>Admin action:</strong> No manual intervention needed - system manages automatically</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Refund & Access Termination</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Policy:</strong> Users who receive refunds lose access immediately</p>
              <p><strong>What happens after refund:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Account status changes to "Expired"</li>
                <li>All access to premium features is revoked</li>
                <li>Continued use after refund = Terms violation</li>
                <li>Legal action may be taken for unauthorized access</li>
              </ul>
              <p><strong>Admin responsibility:</strong> Ensure user account is set to expired after refund processing</p>
              <p><strong>Reference:</strong> See Terms & Conditions and Refund Policy for details</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Section */}
      <Card>
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <TrendingUp className="w-5 h-5" />
            Analytics Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Key Metrics Overview</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Dashboard displays:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Total Users:</strong> All registered accounts</li>
                <li><strong>Active Subscribers:</strong> Users with active paid subscriptions</li>
                <li><strong>Trial Users:</strong> Users currently in free trial period</li>
                <li><strong>Total Revenue:</strong> Sum of all recorded payments</li>
                <li><strong>Upcoming Expirations:</strong> Trials/subscriptions expiring soon</li>
              </ul>
              <p><strong>Use for:</strong> Monitoring growth, tracking revenue, identifying users needing attention</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" />
              App Usage Tracking
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Monitor how users interact with the app</p>
              <p><strong>How to access:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Analytics</strong> tab</li>
                <li>Click on <strong>"App Usage"</strong> sub-tab</li>
                <li>Select time period (7, 14, 30, 60, or 90 days)</li>
              </ol>
              <p><strong>Metrics tracked:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Logins:</strong> Number of user logins</li>
                <li><strong>AI Queries:</strong> Questions asked to AI assistant</li>
                <li><strong>Library Views:</strong> Law reference page visits</li>
                <li><strong>Wizard Uses:</strong> Mode Selection Wizard usage</li>
                <li><strong>Zap Tools:</strong> Timeline Calculator, Section Equivalencies, etc.</li>
              </ul>
              <p><strong>Additional features:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Real-time stats (last 24 hours, last hour)</li>
                <li>Daily activity trend chart</li>
                <li>Top 20 most active users</li>
                <li>Recent activity feed</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Knowledge Management Section */}
      <Card>
        <CardHeader className="bg-indigo-50">
          <CardTitle className="flex items-center gap-2 text-indigo-900">
            <Brain className="w-5 h-5" />
            AI Knowledge Management (Superuser Only)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Managing AI Knowledge Sources</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Add, edit, or remove knowledge sources that the AI assistant uses to answer questions</p>
              <p><strong>How to access:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>AI Knowledge</strong> tab (only visible to superuser)</li>
                <li>View all current knowledge sources</li>
              </ol>
              <p className="text-amber-700">⚠️ <strong>Superuser only:</strong> Only maridjo@gmail.com can manage AI knowledge</p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Adding a Text Note</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Click <strong>"Add Note"</strong> button</li>
                <li>Enter a <strong>Title</strong> (e.g., "New GPPB Circular 2025")</li>
                <li>Enter the <strong>Content</strong> (the knowledge text)</li>
                <li>Add <strong>Keywords</strong> (comma-separated, e.g., "gppb, circular, 2025")</li>
                <li>Set <strong>Priority</strong> (High = checked first by AI)</li>
                <li>Click <strong>"Create Note"</strong></li>
              </ol>
              <p><strong>Keywords help the AI find relevant content when users ask questions</strong></p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Uploading Documents</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Supported formats:</strong> PDF, DOCX, TXT, MD, XLSX, XLS</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Click <strong>"Upload"</strong> button</li>
                <li>Enter a <strong>Title</strong></li>
                <li>Select <strong>File</strong> from your computer</li>
                <li>Add <strong>Keywords</strong></li>
                <li>Set <strong>Priority</strong></li>
                <li>Click <strong>"Upload & Process"</strong></li>
              </ol>
              <p><strong>The system automatically extracts text from the document</strong></p>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Managing Knowledge Sources</h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Enable/Disable:</strong> Toggle the switch to include/exclude from AI responses</p>
              <p><strong>Edit:</strong> Click pencil icon to modify title, content, keywords, or priority</p>
              <p><strong>View:</strong> Click eye icon to see full content and version history</p>
              <p><strong>Delete:</strong> Click trash icon to permanently remove</p>
              <p><strong>Export:</strong> Click download icon to backup all sources as JSON</p>
              <p><strong>Migrate:</strong> Click refresh icon to import static files to database (one-time)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-900">
            <FileText className="w-5 h-5" />
            Quick Reference
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">User Status Types</h4>
              <ul className="space-y-1 text-gray-700">
                <li><strong>Trial:</strong> In free 10-day trial</li>
                <li><strong>Active:</strong> Paid subscriber</li>
                <li><strong>Expired:</strong> Trial/subscription ended</li>
                <li><strong>Banned:</strong> Account suspended</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Pricing</h4>
              <ul className="space-y-1 text-gray-700">
                <li><strong>First Year:</strong> ₱699 + VAT</li>
                <li><strong>Renewal:</strong> ₱99 + VAT</li>
                <li><strong>Trial:</strong> 10 days free</li>
                <li><strong>Extension:</strong> 5 days (one-time)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Admin Roles</h4>
              <ul className="space-y-1 text-gray-700">
                <li><strong>Superuser:</strong> maridjo@gmail.com</li>
                <li><strong>Can promote/demote:</strong> Superuser only</li>
                <li><strong>Can grant free sub:</strong> Superuser only</li>
                <li><strong>Can revoke sub:</strong> Superuser only</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Support Contact</h4>
              <ul className="space-y-1 text-gray-700">
                <li><strong>Email:</strong> support@lexdoright.com</li>
                <li><strong>Website:</strong> lexdoright.com</li>
                <li><strong>Admin Email:</strong> admin@lexdoright.com</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Announcements & Notifications */}
      <Card>
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <Mail className="w-5 h-5" />
            Announcements & Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-500" />
              Send System Announcements
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Broadcast important updates to users via in-app notifications and email</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Announcements</strong> tab</li>
                <li>Enter announcement <strong>Title</strong> (max 100 characters)</li>
                <li>Write your <strong>Message</strong> (max 1000 characters)</li>
                <li>Select <strong>Recipient Type</strong>:
                  <ul className="list-disc pl-5 mt-1">
                    <li><strong>All Users:</strong> Everyone in the system</li>
                    <li><strong>Active Subscribers:</strong> Only paid/trial users</li>
                    <li><strong>Trial Users:</strong> Users on trial period</li>
                    <li><strong>Expired Users:</strong> Expired subscriptions</li>
                  </ul>
                </li>
                <li>Toggle <strong>"Send Email"</strong> if you want email notifications (optional)</li>
                <li>Click <strong>"Send Announcement"</strong></li>
              </ol>
              <p><strong>Use cases:</strong> System updates, maintenance notices, new features, policy changes</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-blue-900 text-xs">
                  <strong>💡 Tip:</strong> Keep announcements concise and clear. Users are more likely to read short messages.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-500" />
              View & Manage Sent Announcements
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Edit or delete previously sent announcements</p>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Scroll down to <strong>"Sent Announcements"</strong> section</li>
                <li>View list of all sent announcements with recipient counts</li>
                <li>Click <strong>Edit (pencil icon)</strong> to modify announcement</li>
                <li>Click <strong>Delete (trash icon)</strong> to remove announcement</li>
              </ol>
              <p><strong>Features:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Updates apply to all users who received it</li>
                <li>Delete removes from all users' notification pages</li>
                <li>Shows how many users received each announcement</li>
                <li>Displays sent date and time</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                <p className="text-amber-900 text-xs">
                  <strong>⚠️ Note:</strong> Editing/deleting announcements affects ALL users who received them.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Management */}
      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Shield className="w-5 h-5" />
            Session Management
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-500" />
              Monitor Active Session Count
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Feature:</strong> View how many active sessions each user has</p>
              <p><strong>How to view:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Look at the <strong>"Sessions"</strong> column in the user table</li>
                <li>Badge shows format: <strong>X/2</strong> (e.g., "2/2" or "1/2")</li>
              </ol>
              <p><strong>Badge colors:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-blue-600">Blue badge (2/2):</strong> User has reached session limit</li>
                <li><strong className="text-gray-600">Gray badge (0/2 or 1/2):</strong> User has available session slots</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-blue-900 text-xs">
                  <strong>💡 Quick Identification:</strong> Easily spot users who may need session clearing without waiting for reports.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Lock className="w-4 h-4 text-blue-500" />
              Clear User Sessions (Admin Action)
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Purpose:</strong> Help users who are locked out due to the 2-session limit (last resort)</p>
              <p><strong>When to use:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>User reports "Session limit exceeded" error</li>
                <li>User closed browser without logging out properly</li>
                <li>User switched browsers and can't access old sessions</li>
                <li>Sessions stuck due to browser cache issues</li>
              </ul>
              <p><strong>Steps:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Go to <strong>Users</strong> tab</li>
                <li>Check <strong>"Sessions"</strong> column to see active sessions (e.g., "2/2")</li>
                <li>Find the affected user and click <strong>Manage</strong></li>
                <li>Select <strong>"Clear Sessions"</strong> from dropdown</li>
                <li>Review the information about what will happen</li>
                <li>Click <strong>"🔄 Clear All Sessions"</strong></li>
                <li>Inform user they can now log in</li>
              </ol>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                <p className="text-green-900 text-xs">
                  <strong>✓ Safe Action:</strong> Only clears login sessions. Does not affect user data, subscription, or bookmarks.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-purple-500" />
              Admin Session Exemption
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Admin Privilege:</strong> Admin accounts (including superuser) are exempt from the 2-session limit</p>
              <p><strong>Benefits:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>✅ Unlimited sessions for admins</li>
                <li>✅ No "Session limit exceeded" errors</li>
                <li>✅ Log in from multiple browsers simultaneously</li>
                <li>✅ Better support and troubleshooting capabilities</li>
              </ul>
              <p><strong>Regular users:</strong> Still limited to 2 concurrent sessions for security</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-blue-900 text-xs">
                  <strong>💡 User Self-Service:</strong> Users can clear their own sessions via Settings → Sessions. 
                  <strong className="text-red-600"> They will be logged out immediately</strong> after clearing, preventing bypass of the 2-session limit.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Security Enhancement: Auto-Logout After Clear
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>How it works:</strong> When users clear their sessions via Settings, they are immediately logged out</p>
              <p><strong>Why this matters:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Prevents users from bypassing the 2-session limit</li>
                <li>User cannot stay logged in after clearing sessions</li>
                <li>Forces proper re-authentication</li>
                <li>Maintains account security integrity</li>
              </ul>
              <p><strong>User experience:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>User clicks "Clear All Sessions"</li>
                <li>Confirms action with warning about logout</li>
                <li>All sessions cleared (including current)</li>
                <li>Toast message: "Logging you out..."</li>
                <li>Automatic redirect to home page after 1.5 seconds</li>
                <li>Must log in again to access account</li>
              </ol>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                <p className="text-green-900 text-xs">
                  <strong>✓ Result:</strong> Session limit enforcement is now effective. Users cannot repeatedly clear sessions to stay logged in on unlimited sessions.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Troubleshooting Session Issues
            </h3>
            <div className="pl-6 space-y-2 text-sm text-gray-700">
              <p><strong>Common Issues & Solutions:</strong></p>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded p-3">
                  <p className="font-semibold text-gray-900">Issue: User can't log in after clearing sessions</p>
                  <p className="text-xs mt-1">
                    <strong>Solution:</strong> Check if new sessions were created. Clear again via Admin Dashboard. Verify user account isn't banned or expired.
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="font-semibold text-gray-900">Issue: Sessions keep getting stuck for same user</p>
                  <p className="text-xs mt-1">
                    <strong>Solution:</strong> User may be closing browser without logout. Educate user on proper logout. Show them Settings → Sessions self-service option.
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="font-semibold text-gray-900">Issue: Admin account getting session limit error</p>
                  <p className="text-xs mt-1">
                    <strong>Solution:</strong> This shouldn't happen. Verify admin role in database. Check backend logs. Contact development team if issue persists.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <CheckCircle className="w-5 h-5" />
            Best Practices & Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Review extension requests within 24 hours</strong> - Users are expecting prompt responses</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Use "Grant Full Access" for standard paid subscriptions</strong> - Select 365 days (1 year) for regular customers</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Reserve "Free 30-Day Subscription" for special cases</strong> - Beta testers, partners, service recovery</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Document ban reasons clearly</strong> - Required for legal purposes and user appeals</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Verify payment before activating</strong> - Always confirm payment receipt before granting access</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Revoke subscription only when necessary</strong> - This action is immediate and affects user access</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Use announcements for important updates only</strong> - Don't spam users with frequent announcements</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Clear sessions promptly when users report issues</strong> - Fast response improves user satisfaction</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Direct users to self-service options first</strong> - Show users Settings → Sessions before admin intervention</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
